import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
  Divider,
  Paper,
  alpha,
} from "@mui/material";
import {
  FaUserGraduate,
  FaUniversity,
  FaHandHoldingUsd,
  FaChartLine,
  FaIdCard,
  FaEnvelope,
  FaGraduationCap,
} from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/axiosInstance";
import { DonorUserResponse, TaggedApplicant } from "../../typings/users";
import { ScholarshipApplicationsListingProps } from "../../typings/scholarship-applications";
import { motion } from "framer-motion";
import theme from "../../theme";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

// Elegant compact stat card component
const StatCard = ({
  title,
  value,
  icon,
  color,
  delay = 0,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  delay?: number;
}) => (
  <MotionCard
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    elevation={0}
    sx={{
      height: "100%",
      background: "white",
      borderRadius: 2.5,
      border: `1px solid ${alpha(color, 0.15)}`,
      position: "relative",
      overflow: "hidden",
      transition: "all 0.25s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
        borderColor: alpha(color, 0.3),
      },
    }}
  >
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: alpha(color, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h4"
            fontWeight="700"
            sx={{
              color: theme.palette.colors.gray["900"],
              fontSize: "24px",
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.colors.gray["500"],
              fontWeight: 500,
              fontSize: "13px",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </MotionCard>
);

// Custom tooltip component for the chart
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          background: "white",
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          border: `1px solid ${theme.palette.colors.gray["100"]}`,
          p: 2,
          minWidth: 140,
        }}
      >
        <Typography
          variant="body2"
          fontWeight="600"
          sx={{ color: theme.palette.colors.gray["900"], mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography
          variant="h5"
          fontWeight="700"
          sx={{ color: theme.palette.primary.main }}
        >
          {payload[0].value.toFixed(2)} GPA
        </Typography>
      </Box>
    );
  }
  return null;
};

const DonorDashboard = () => {
  const auth = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [donorData, setDonorData] = useState<DonorUserResponse | null>(null);
  const [selectedStudent, setSelectedStudent] =
    useState<TaggedApplicant | null>(null);
  const [studentPerformance, setStudentPerformance] = useState<
    { semester: string; gpa: number; fullData: any }[]
  >([]);
  const [loadingPerformance, setLoadingPerformance] = useState(false);

  // Fetch Donor Data
  useEffect(() => {
    const fetchDonorData = async () => {
      if (!auth?.userId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await api.get(`/users/${auth.userId}/`);
        if (response.data?.data) {
          setDonorData(response.data.data);
          if (
            response.data.data.tagged_applicants &&
            response.data.data.tagged_applicants.length > 0
          ) {
            setSelectedStudent(response.data.data.tagged_applicants[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching donor data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDonorData();
  }, [auth?.userId]);

  // Fetch Student Performance when a student is selected
  useEffect(() => {
    const fetchStudentPerformance = async () => {
      if (!selectedStudent) return;
      try {
        setLoadingPerformance(true);
        const response = await api.get(
          // @ts-ignore
          `/scholarship-applications?applicant_id=${selectedStudent.id}`
        );
        const applications: ScholarshipApplicationsListingProps[] =
          response.data?.data || [];

        const chartData = applications
          .map((app: any) => ({
            semester: `Sem ${app.last_semester || "?"}`,
            gpa: app.last_cgpa ? parseFloat(app.last_cgpa) : 0,
            fullData: app,
          }))
          .sort((a, b) => {
            const semA = parseInt(a.semester.replace(/\D/g, "")) || 0;
            const semB = parseInt(b.semester.replace(/\D/g, "")) || 0;
            return semA - semB;
          });

        setStudentPerformance(chartData);
      } catch (error) {
        console.error("Error fetching student performance:", error);
        setStudentPerformance([]);
      } finally {
        setLoadingPerformance(false);
      }
    };

    fetchStudentPerformance();
  }, [selectedStudent]);

  const distinctUniversities = useMemo(() => {
    if (!donorData?.tagged_institutes) return [];
    return [...new Set(donorData.tagged_institutes)];
  }, [donorData]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (!donorData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
        gap={2}
      >
        <Typography variant="h6" color="error">
          Unable to load donor data.
        </Typography>
        <Typography variant="body1">
          Please log out and log in again to refresh your session.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Welcome Section */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            padding: "10px 0px 15px 0px",
            mb: 1
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "primary.main", mb: 0.5 }}
          >
            Welcome, {donorData?.name?.split(" ")[0]} 👋
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.colors.gray["500"],
              fontSize: "14px",
            }}
          >
            Track your scholarship impact and student progress
          </Typography>
        </Box>

        {/* Compact Summary Cards */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={4}>
            <StatCard
              title="Students Supported"
              value={donorData?.tagged_applicants?.length || 0}
              icon={<FaUserGraduate size={20} />}
              color={theme.palette.primary.main}
              delay={0.1}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title="Universities"
              value={distinctUniversities.length}
              icon={<FaUniversity size={20} />}
              color={theme.palette.colors.purple["600"]}
              delay={0.15}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title="Total Donated"
              value={`PKR ${(
                donorData?.total_amount_donated || 0
              ).toLocaleString()}`}
              icon={<FaHandHoldingUsd size={20} />}
              color={theme.palette.colors.blue["600"]}
              delay={0.2}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2.5}>
          {/* Students Section */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.colors.gray["200"]}`,
                background: "white",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <Box display="flex" alignItems="center" gap={1.2} mb={2}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <FaUserGraduate size={14} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="700"
                    sx={{ color: theme.palette.colors.gray["800"], fontSize: "14px" }}
                  >
                    Students Supported
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(donorData?.tagged_applicants || []).map((student, index) => (
                    <Chip
                      key={`student-${index}`}
                      label={student.name}
                      size="small"
                      onClick={() => setSelectedStudent(student)}
                      sx={{
                        borderRadius: 1.5,
                        fontWeight: 600,
                        fontSize: "12px",
                        height: "32px",
                        px: 0.5,
                        background:
                          selectedStudent?.id === student.id
                            ? alpha(theme.palette.primary.main, 0.12)
                            : "white",
                        color:
                          selectedStudent?.id === student.id
                            ? theme.palette.primary.main
                            : theme.palette.colors.gray["700"],
                        border:
                          selectedStudent?.id === student.id
                            ? `1.5px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.colors.gray["200"]}`,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: alpha(theme.palette.primary.main, 0.05),
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  ))}
                  {(donorData?.tagged_applicants || []).length === 0 && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.colors.gray["400"], fontStyle: "italic", py: 0.5 }}
                    >
                      No students linked
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Institutions Section */}
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.colors.gray["200"]}`,
                background: "white",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <Box display="flex" alignItems="center" gap={1.2} mb={2}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.colors.purple["600"], 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.colors.purple["600"],
                    }}
                  >
                    <FaUniversity size={14} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="700"
                    sx={{ color: theme.palette.colors.gray["800"], fontSize: "14px" }}
                  >
                    Institutions
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {distinctUniversities.map((uni, index) => (
                    <Chip
                      key={`uni-${index}`}
                      label={uni}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        fontWeight: 600,
                        fontSize: "12px",
                        height: "32px",
                        px: 0.5,
                        background: alpha(theme.palette.colors.purple["600"], 0.06),
                        color: theme.palette.colors.purple["700"],
                        border: `1px solid ${alpha(theme.palette.colors.purple["600"], 0.2)}`,
                        "&:hover": {
                          background: alpha(theme.palette.colors.purple["600"], 0.1),
                        },
                      }}
                    />
                  ))}
                  {distinctUniversities.length === 0 && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.colors.gray["400"], fontStyle: "italic", py: 0.5 }}
                    >
                      No institutions linked
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>
        </Grid>

        {/* Main Content Area */}
        <Grid container spacing={2.5} alignItems="stretch">
          {/* Left Column: Performance Chart */}
          <Grid item xs={12} lg={8}>
            <MotionCard
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.colors.gray["200"]}`,
                height: "100%",
                background: "white",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                  mb={2.5}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        background: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <FaChartLine size={16} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        sx={{
                          color: theme.palette.colors.gray["900"],
                          fontSize: "16px",
                          lineHeight: 1.3,
                        }}
                      >
                        Academic Performance
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.colors.gray["500"],
                          fontSize: "12px",
                        }}
                      >
                        CGPA progression over semesters
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      Search & view student progress →
                    </Typography>
                    <Autocomplete
                      options={donorData?.tagged_applicants || []}
                      getOptionLabel={(option) => option.name}
                      value={selectedStudent}
                      onChange={(_, newValue) => setSelectedStudent(newValue)}
                      size="small"
                      sx={{ minWidth: 220 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search applicant..."
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: theme.palette.colors.gray["50"],
                              fontSize: "13px",
                              "& fieldset": {
                                borderColor: theme.palette.colors.gray["200"],
                              },
                              "&:hover fieldset": {
                                borderColor: theme.palette.primary.main,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {/* Chart Area */}
                <Box height={300} width="100%">
                  {loadingPerformance ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    </Box>
                  ) : studentPerformance.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={studentPerformance}
                        margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
                        barCategoryGap="25%"
                      >
                        <defs>
                          <linearGradient
                            id="barGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={theme.palette.primary.main}
                              stopOpacity={1}
                            />
                            <stop
                              offset="100%"
                              stopColor={theme.palette.colors.green["700"]}
                              stopOpacity={0.9}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke={theme.palette.colors.gray["200"]}
                        />
                        <XAxis
                          dataKey="semester"
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: theme.palette.colors.gray["600"],
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                          dy={10}
                        />
                        <YAxis
                          domain={[0, 4]}
                          axisLine={false}
                          tickLine={false}
                          tick={{
                            fill: theme.palette.colors.gray["600"],
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                          dx={-5}
                          tickFormatter={(value) => value.toFixed(1)}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                        <ReferenceLine
                          y={3.0}
                          stroke={theme.palette.colors.green["400"]}
                          strokeDasharray="4 4"
                          strokeWidth={1.5}
                          // label={{
                          //   // value: "Honors",
                          //   position: "right",
                          //   fill: theme.palette.colors.green["600"],
                          //   fontSize: 11,
                          //   fontWeight: 600,
                          // }}
                        />
                        <Bar
                          dataKey="gpa"
                          fill="url(#barGradient)"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={50}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                      bgcolor={theme.palette.colors.gray["50"]}
                      borderRadius={2}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: theme.palette.colors.gray["100"],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1.5,
                        }}
                      >
                        <FaChartLine
                          size={24}
                          color={theme.palette.colors.gray["400"]}
                        />
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.colors.gray["700"],
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        No Data Available
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.colors.gray["500"],
                          textAlign: "center",
                          maxWidth: 260,
                        }}
                      >
                        {selectedStudent
                          ? "No academic records found for this student."
                          : "Select a student to view performance data."}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Right Column: Student Details */}
          <Grid item xs={12} lg={4}>
            <MotionBox
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ height: "100%" }}
            >
              {/* Student Profile Card */}
              {selectedStudent ? (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: `1px solid ${theme.palette.colors.gray["200"]}`,
                    overflow: "hidden",
                    background: "white",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.colors.green["700"]} 100%)`,
                      p: 2.5,
                      position: "relative",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          background: "rgba(255,255,255,0.2)",
                          fontSize: 20,
                          fontWeight: 700,
                          border: "2px solid rgba(255,255,255,0.3)",
                        }}
                      >
                        {selectedStudent.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box flex={1}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="700"
                          sx={{ color: "white", fontSize: "15px", mb: 0.25 }}
                        >
                          {selectedStudent.name}
                        </Typography>
                        <Chip
                          label="Scholarship Recipient"
                          size="small"
                          sx={{
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "10px",
                            height: "20px",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: alpha(
                              theme.palette.colors.indigo["600"],
                              0.1
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <FaIdCard
                            size={14}
                            color={theme.palette.colors.indigo["600"]}
                          />
                        </Box>
                        <Box flex={1}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.colors.gray["500"],
                              fontSize: "11px",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                            }}
                          >
                            Student ID
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{ color: theme.palette.colors.gray["900"] }}
                          >
                            #{selectedStudent.id}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            background: alpha(
                              theme.palette.colors.pink["600"],
                              0.1
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <FaEnvelope
                            size={14}
                            color={theme.palette.colors.pink["600"]}
                          />
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.colors.gray["500"],
                              fontSize: "11px",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                            }}
                          >
                            Email
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                              color: theme.palette.colors.gray["700"],
                              wordBreak: "break-all",
                              fontSize: "13px",
                            }}
                          >
                            {selectedStudent.email}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Academic Summary */}
                      {studentPerformance.length > 0 &&
                        studentPerformance[studentPerformance.length - 1]
                          .fullData && (
                          <Box
                            sx={{
                              background: alpha(theme.palette.primary.main, 0.06),
                              borderRadius: 2,
                              p: 2,
                              mt: 0.5,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                              <FaGraduationCap
                                size={14}
                                color={theme.palette.primary.main}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.colors.gray["600"],
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.3px",
                                }}
                              >
                                Academic Summary
                              </Typography>
                            </Box>
                            <Box display="flex" gap={3}>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.colors.gray["500"],
                                    fontSize: "11px",
                                  }}
                                >
                                  Semester
                                </Typography>
                                <Typography
                                  variant="h6"
                                  fontWeight="700"
                                  sx={{
                                    color: theme.palette.colors.gray["900"],
                                    fontSize: "18px",
                                  }}
                                >
                                  {
                                    studentPerformance[
                                      studentPerformance.length - 1
                                    ].fullData.last_semester
                                  }
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.colors.gray["500"],
                                    fontSize: "11px",
                                  }}
                                >
                                  Current CGPA
                                </Typography>
                                <Typography
                                  variant="h6"
                                  fontWeight="700"
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: "18px",
                                  }}
                                >
                                  {studentPerformance[
                                    studentPerformance.length - 1
                                  ].gpa.toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderRadius: 2.5,
                    border: `1px dashed ${theme.palette.colors.gray["300"]}`,
                    background: "transparent",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: theme.palette.colors.gray["100"],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                    }}
                  >
                    <FaUserGraduate
                      size={20}
                      color={theme.palette.colors.gray["400"]}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{ color: theme.palette.colors.gray["700"], mb: 0.25 }}
                  >
                    No Student Selected
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.colors.gray["500"] }}
                  >
                    Select a student to view details
                  </Typography>
                </Paper>
              )}
            </MotionBox>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DonorDashboard;
